/**
 * Care-event display labels (PURE).
 *
 * Kept out of ClientDetailDrawer so it can be unit-tested: importing the drawer
 * pulls in `lib/firebase`, which initialises Firebase Auth and throws in the test
 * environment. Same reason `trailRiskRefState.ts` is separate on the server.
 */

import type { ClientDetailEvent } from '../../hooks/useClientDetail';

export const EVENT_LABELS: Record<string, string> = {
  settlement_anniversary: 'Settlement anniversary',
  birthday: 'Birthday',
  periodic_review: 'Periodic review',
  post_settlement: 'Post-settlement',
  refinance_sweet_spot: 'Refinance review',
  fixed_rate_expiry: 'Fixed-rate expiry',
  io_expiry: 'Interest-only expiry',
  intro_rate_expiry: 'Intro-rate expiry',
  loan_maturity: 'Loan maturity',
  pre_approval_expiry: 'Pre-approval expiry',
  bid_annual: 'Annual review (BID)',
  dormant: 'Re-engagement',
  follow_up: 'Follow-up',
  custom: 'Custom',
  // Trail-risk family. These were missing, so the drawer rendered raw snake_case
  // ("trail_dropped_off") straight at the user. Invisible while only one tenant
  // had trail-risk events; live for every enabled tenant now.
  loan_discharged: 'Discharged loan follow-up',
  arrears_commission: 'Loan in arrears',
  clawback: 'Commission clawback',
  trail_dropped_off: 'Trail payment stopped',
  trail_stopped: 'No trail for two months or more',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Jul 2026" from 'YYYY-MM'. Returns '' for anything malformed.
 *
 * A fixed month table rather than `toLocaleDateString`: ICU's en-AU "short" month
 * is not stable across builds — Node renders June and July in full while the
 * browser abbreviates — so the same event would be labelled differently depending
 * on where the code ran. A period label has no business varying by environment.
 */
export function monthLabel(period: string | null | undefined): string {
  if (!period || !/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) return '';
  const [y, m] = period.split('-');
  return `${MONTHS[Number(m) - 1]} ${y}`;
}

/**
 * Trail-risk events are per loan AND per period, so the label has to say WHICH
 * occurrence. Two open arrears months on one client are otherwise two identical
 * rows, and there is no way to tell which one an action applied to.
 */
export function eventTitle(ev: ClientDetailEvent): string {
  const base = EVENT_LABELS[ev.eventType] ?? ev.eventType;
  if (ev.eventFamily !== 'trail_risk') return base;
  const parts = [monthLabel(ev.trailRiskPeriodMonth), ev.trailRiskLoanId ? `loan ${ev.trailRiskLoanId}` : '']
    .filter(Boolean)
    .join(' · ');
  return parts ? `${base} · ${parts}` : base;
}
